import React from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SupplierOrderItemDto } from '../dto/supplierOrder';
import { formatProductDimensions } from '../utils/productDimensions';
import {
  asCommercialUnit,
  convertCommercialQuantity,
  getPersistedQuantityForUnit,
} from '../utils/commercialQuantity';
import type { AllocationUnit } from '../utils/dimensionConversion';
import { formatUnit } from '../utils/units';
import { formatSupplierOrderQuantityValue } from '../modules/orders/supplierOrderUnits';
import './TransportSupplierOrderLines.css';

const { Text } = Typography;

/**
 * ZD lines inside the transport form: full product name (never truncated),
 * dimensions, and the ordered quantity in pieces plus one extra unit when it
 * can be derived. The persisted quantity set (quantityPcs/M2/M3) is the
 * first source, product dimensions the fallback.
 */
interface TransportSupplierOrderLinesProps {
  items?: SupplierOrderItemDto[];
}

export function resolveSupplierLineQuantity(
  item: SupplierOrderItemDto,
  unit: AllocationUnit,
): number | null {
  const ownUnit = asCommercialUnit(item.unit, 'pcs');
  const ownQuantity = Number(item.quantity);
  if (!Number.isFinite(ownQuantity)) return null;
  if (unit === ownUnit) return ownQuantity;

  const persisted = getPersistedQuantityForUnit(item as unknown as Record<string, unknown>, unit);
  if (persisted != null && Number.isFinite(persisted)) return persisted;

  const converted = convertCommercialQuantity(ownQuantity, ownUnit, unit, {
    width: item.width ?? null,
    length: item.length ?? null,
    thickness: item.thickness ?? null,
  });
  return converted.value;
}

/**
 * Pieces first, then one extra unit when it can be derived: the ZD's own unit
 * when that is not pieces, otherwise m² (falling back to m³).
 */
export function resolveSupplierLineDisplay(
  item: SupplierOrderItemDto,
): { primary: { unit: AllocationUnit; quantity: number } | null; secondary: { unit: AllocationUnit; quantity: number } | null } {
  const ownUnit = asCommercialUnit(item.unit, 'pcs');
  const pcs = resolveSupplierLineQuantity(item, 'pcs');
  const primary = pcs != null
    ? { unit: 'pcs' as AllocationUnit, quantity: pcs }
    : (Number.isFinite(Number(item.quantity)) ? { unit: ownUnit, quantity: Number(item.quantity) } : null);
  if (!primary) return { primary: null, secondary: null };

  const extraCandidates: AllocationUnit[] = ownUnit !== 'pcs' ? [ownUnit] : ['m2', 'm3'];
  for (const unit of extraCandidates) {
    if (unit === primary.unit) continue;
    const quantity = resolveSupplierLineQuantity(item, unit);
    if (quantity != null) return { primary, secondary: { unit, quantity } };
  }
  return { primary, secondary: null };
}

const TransportSupplierOrderLines: React.FC<TransportSupplierOrderLinesProps> = ({ items }) => {
  const { i18n } = useTranslation(['transport']);

  if (!items?.length) {
    return <Text type="secondary">—</Text>;
  }

  return (
    <ul className="transport-zd-lines__list" data-testid="transport-supplier-order-lines">
      {items.map((item, index) => {
        const { primary, secondary } = resolveSupplierLineDisplay(item);
        const dimensions = formatProductDimensions(item);
        return (
          <li key={item.id || `${item.name}-${index}`} className="transport-zd-line">
            <div className="transport-zd-line__copy">
              <Text strong className="transport-zd-line__name">{item.name}</Text>
              {dimensions ? (
                <Text type="secondary" className="transport-zd-line__dimensions">{dimensions}</Text>
              ) : null}
            </div>
            <div className="transport-zd-line__quantity">
              {primary ? (
                <Text strong className="transport-zd-line__quantity-value">
                  {formatSupplierOrderQuantityValue(primary.quantity, i18n.language)} {formatUnit(primary.unit, i18n.language)}
                </Text>
              ) : <Text type="secondary">—</Text>}
              {secondary ? (
                <Text type="secondary" className="transport-zd-line__quantity-source">
                  {formatSupplierOrderQuantityValue(secondary.quantity, i18n.language)} {formatUnit(secondary.unit, i18n.language)}
                </Text>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default TransportSupplierOrderLines;
